import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import FacultyCard from "./FacultyCard";
import DepartmentSelector from "./DepartmentSelector";
import { ArrowLeft } from "lucide-react";

const DepartmentsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [facultyData, setFacultyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(
    location.state?.defaultDepartment || "ECE"
  );
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchFacultyData = async () => {
      if (!selectedDepartment) return;
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(
          `ece/faculty/know-your-faculty?department_id=${selectedDepartment}`,
          {
            baseURL: BACKEND,
            withCredentials: true,
          }
        );
        setFacultyData(response.data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        setFacultyData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFacultyData();
  }, [selectedDepartment, BACKEND]);

  const handleDepartmentChange = (deptId) => {
    setSelectedDepartment(deptId);
    navigate(`?department=${deptId}`, { replace: true });
  };

  // Find the Head of Department (Prof. Neeta Pandey)
  const headOfDepartment = facultyData.find(
    (faculty) => faculty.faculty_name === "Prof. Neeta Pandey"
  );

  // Filter out the HoD from regular faculty
  const regularFaculty = facultyData.filter(
    (faculty) => faculty.faculty_name !== "Prof. Neeta Pandey"
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Back to Home Button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center text-[#800000] hover:text-[#600000] mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-1" />
          Back to Home
        </button>

        <h1 className="text-4xl font-bold text-[#800000] mb-8">
          Know Your Mentors
        </h1>

        <DepartmentSelector
          selectedDepartment={selectedDepartment}
          onChange={handleDepartmentChange}
        />

        {loading && (
          <div className="flex flex-col gap-6 mt-8">
            {[...Array(6)].map((_, i) => (
              <FacultyCardSkeleton key={i} />
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {facultyData.length > 0 ? (
              <div className="mt-8">
                {/* Head of Department Section */}
                {headOfDepartment && (
                  <div className="mb-8">
                    <h2 className="text-xl font-semibold text-[#800000] mb-4">
                      Head of Department
                    </h2>
                    <FacultyCard faculty={headOfDepartment} isHod={true} />
                  </div>
                )}

                {/* Regular Faculty Section */}
                {regularFaculty.length > 0 && (
                  <>
                    <h2 className="text-xl font-semibold text-[#800000] mb-4">
                      Professors
                    </h2>
                    <div className="flex flex-col gap-6">
                      {regularFaculty.map((faculty) => (
                        <FacultyCard
                          key={faculty.faculty_id}
                          faculty={faculty}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  No faculty found
                </h3>
                <p className="mt-1 text-gray-500">
                  {selectedDepartment
                    ? "No faculty members found for this department."
                    : "Please select a department to view faculty."}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const FacultyCardSkeleton = () => (
  <div className="w-full flex items-center bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 animate-pulse">
    <div className="w-28 sm:w-36 h-28 sm:h-36 bg-gray-200 flex-shrink-0"></div>
    <div className="flex flex-col flex-grow p-4">
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
    </div>
  </div>
);

export default DepartmentsPage;
